import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useNavigate } from 'react-router-dom';
import GoogleLoginButton from './GoogleLoginButton.jsx';
import { Button, Card, Text, Badge, Spinner, Icon } from './ui/index.jsx';
import { useCreditBalance } from '../hooks/queries/index.js';

export default function UserMenu({ mobile = false }) {
    const { user, loading, signOut, refreshUserProfile, authType, getAuthInfo, canAccessAPI } = useAuth();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();
    const authInfo = getAuthInfo();

    // Use TanStack Query for fresh credit balance (synced with Dashboard)
    const isAuthenticated = user && authType === 'authenticated';
    const { data: balanceData } = useCreditBalance({ enabled: isAuthenticated });

    // Get credits from balance API (fresh) or fallback to user context (cached)
    const displayCredits = balanceData?.balance?.available ?? user?.credits ?? 0;

    if (loading) {
        return (
            <div className="flex items-center">
                <Spinner size="sm" />
            </div>
        );
    }

    // Show login for unauthenticated users or guests
    if (!user || authType === 'guest') {
        return (
            <div className={`flex items-center ${mobile ? 'flex-col space-y-3' : 'space-x-2'}`}>
                {authType === 'guest' && user && (
                    <div className={`${mobile ? 'text-center' : ''} mb-2`}>
                        <Text variant="muted" size="xs">Currently signed in as guest</Text>
                        <Text variant="accent" size="xs" weight="medium">{user.displayName}</Text>
                    </div>
                )}

                <GoogleLoginButton
                    size={mobile ? "large" : "medium"}
                    variant="primary"
                    className={mobile ? "w-full" : ""}
                    showText={true}
                />

                {!mobile && (
                    <Button
                        onClick={() => navigate('/plans')}
                        variant="ghost"
                        size="sm"
                    >
                        View Plans
                    </Button>
                )}
            </div>
        );
    }

    const handleMenuToggle = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleMenuItemClick = (action) => {
        setIsMenuOpen(false);
        action();
    };

    const getCreditWarningLevel = () => {
        // Only show credit warnings for authenticated users
        // Thresholds: 8 credits = 1 architecture
        if (authType !== 'authenticated' || !displayCredits) return 'normal';
        if (displayCredits <= 8) return 'critical';  // 1 or less architectures
        if (displayCredits < 24) return 'warning';   // Less than 3 architectures
        return 'normal';
    };

    const getCreditDisplay = () => {
        if (authType === 'guest') {
            return (
                <div className="text-xs text-gray-500 px-2 py-1">
                    <Text variant="muted" size="xs">Sign in to track credits</Text>
                </div>
            );
        }

        return (
            <Badge variant={
                getCreditWarningLevel() === 'critical' ? 'error' :
                    getCreditWarningLevel() === 'warning' ? 'warning' :
                        'info'
            }>
                <Icon name="creditCard" size="xs" className="mr-1" />
                <span>{displayCredits.toLocaleString()}</span>
            </Badge>
        );
    };

    // Mobile version: render menu items directly without dropdown
    if (mobile) {
        return (
            <div className="flex flex-col space-y-2">
                {authType === 'authenticated' ? (
                    <>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="text-left px-3 py-2 rounded text-[#CCCCCC] hover:text-white hover:bg-white/5 flex items-center transition-colors"
                        >
                            <Icon name="home" size="xs" className="mr-3" />
                            <Text variant="primary" size="sm">Dashboard</Text>
                        </button>

                        <hr className="my-3 border-[#3A3D46]" />

                        <button
                            onClick={() => signOut()}
                            className="text-left px-3 py-2 rounded text-red-400 hover:text-red-300 hover:bg-red-400/10 flex items-center transition-colors"
                        >
                            <Icon name="signOut" size="xs" className="mr-3" />
                            <Text variant="primary" size="sm">Sign Out</Text>
                        </button>
                    </>
                ) : (
                    <button
                        onClick={() => navigate('/signin')}
                        className="text-left px-3 py-2 rounded text-[#CCCCCC] hover:text-white hover:bg-white/5 flex items-center transition-colors"
                    >
                        <Icon name="lock" size="xs" className="mr-3" />
                        <Text variant="primary" size="sm">Sign In</Text>
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="relative">
            {/* User Button - No hover effects */}
            <button
                onClick={handleMenuToggle}
                className="flex items-center space-x-3 p-2 rounded-lg"
            >
                {/* User Avatar */}
                <div className="h-8 w-8 rounded-full bg-[#7B61FF]/20 flex items-center justify-center">
                    <span className="text-[#7B61FF] font-medium text-sm">
                        {user.displayName.charAt(0).toUpperCase()}
                    </span>
                </div>

                {/* User Info - Desktop */}
                <div className="hidden md:block text-left">
                    <Text variant="primary" size="sm" weight="medium">{user.displayName}</Text>
                    <Text variant="secondary" size="xs" className="capitalize">
                        {authType === 'guest' ? 'Guest User' : `${user.plan || 'Free'} Plan`}
                    </Text>
                </div>

                {/* Dropdown Arrow */}
                <svg
                    className={`h-4 w-4 text-gray-400 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 py-1 z-50 bg-[#0A0A0A] border border-[#3A3D46] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-[#3A3D46]">
                        <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-[#7B61FF]/20 flex items-center justify-center">
                                <span className="text-[#7B61FF] font-medium">
                                    {user.displayName.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1">
                                <Text variant="primary" size="sm" weight="medium">{user.displayName}</Text>
                                <Text variant="secondary" size="xs">{user.email || 'Guest User'}</Text>
                                <div className="flex items-center mt-1">
                                    <Text variant="muted" size="xs" className="capitalize mr-2">
                                        {authType === 'guest' ? 'Guest User' : `${user.plan || 'Free'} Plan`}
                                    </Text>
                                    {authType === 'authenticated' ? (
                                        <Badge variant={
                                            getCreditWarningLevel() === 'critical' ? 'error' :
                                                getCreditWarningLevel() === 'warning' ? 'warning' :
                                                    'info'
                                        } size="xs">
                                            {displayCredits.toLocaleString()} credits
                                        </Badge>
                                    ) : (
                                        <Text variant="muted" size="xs">
                                            Sign in for credits
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Menu Items - No hover effects */}
                    <div className="py-1">
                        {authType === 'authenticated' ? (
                            <>
                                <button
                                    onClick={() => handleMenuItemClick(() => navigate('/dashboard'))}
                                    className="w-full text-left px-4 py-2 flex items-center"
                                >
                                    <Icon name="home" size="xs" className="mr-3" />
                                    <Text variant="primary" size="sm">Dashboard</Text>
                                </button>

                                <button
                                    onClick={() => handleMenuItemClick(() => navigate('/dashboard?tab=billing'))}
                                    className="w-full text-left px-4 py-2 flex items-center"
                                >
                                    <Icon name="settings" size="xs" className="mr-3" />
                                    <Text variant="primary" size="sm">Settings</Text>
                                </button>

                                <button
                                    onClick={() => handleMenuItemClick(refreshUserProfile)}
                                    className="w-full text-left px-4 py-2 flex items-center"
                                >
                                    <Icon name="refresh" size="xs" className="mr-3" />
                                    <Text variant="primary" size="sm">Refresh Profile & Credits</Text>
                                </button>

                                <hr className="my-1 border-[#3A3D46]" />

                                <button
                                    onClick={() => handleMenuItemClick(signOut)}
                                    className="w-full text-left px-4 py-2 flex items-center"
                                >
                                    <Icon name="signOut" size="xs" className="mr-3" />
                                    <Text variant="primary" size="sm">Sign Out</Text>
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => handleMenuItemClick(() => navigate('/plans'))}
                                    className="w-full text-left px-4 py-2 flex items-center"
                                >
                                    <Icon name="eye" size="xs" className="mr-3" />
                                    <Text variant="primary" size="sm">View Plans</Text>
                                </button>

                                <hr className="my-1 border-[#3A3D46]" />

                                <div className="px-4 py-2">
                                    <GoogleLoginButton
                                        size="small"
                                        variant="primary"
                                        className="w-full"
                                        showText={true}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                </div>
            )}

            {/* Overlay to close menu */}
            {isMenuOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsMenuOpen(false)}
                ></div>
            )}
        </div>
    );
}